import React, { useRef, useState } from "react";
import { useLocation } from "react-router";
import { toast } from "react-toastify";
import { useContextMenu } from "../../Layout Component/ContextMenu";
import { copyText } from "../../utils/printDoc";
import Conditions from "../Conditions";
import MedicalHistoryNav, { EmbeddedContext } from "./MedicalHistoryNav";
import PreviousFertilityTreatment from "./PreviousFertilityTreatment";
import PreviousPregnancy from "./PreviousPregnancy";
import PreviousProcedure from "./PreviousProcedure";

// ── Previous history ─────────────────────────────────────────────────────────
//
// Previous fertility treatment, previous pregnancies, previous procedures and
// conditions on one tab. Each list is the grid it always was, with its own add,
// edit and delete — shown one at a time, because their tables share an id that
// the grid library cannot hold twice on a page.
//
// What this page adds, on any row of any list:
//   double-click — edit the row
//   right-click  — Edit, Delete, Add new, Copy row
// It works by pressing the row's own pencil / bin / Add button, so the save,
// validation and API calls are exactly those of the original pages.

const LISTS = [
  { key: "fertility", label: "Fertility treatment", path: "PreviousFertilityTreatment", Component: PreviousFertilityTreatment, noun: "treatment" },
  { key: "pregnancy", label: "Pregnancies", path: "PreviousPregnancy", Component: PreviousPregnancy, noun: "pregnancy" },
  { key: "procedure", label: "Procedures", path: "PreviousProcedure", Component: PreviousProcedure, noun: "procedure" },
  { key: "conditions", label: "Conditions", path: "Conditions", Component: Conditions, noun: "condition" },
];

const patient = () => { try { return JSON.parse(localStorage.getItem("UData")); } catch { return null; } };

export default function PreviousHistory() {
  const { state } = useLocation();
  const [active, setActive] = useState(state?.list || "fertility");
  const box = useRef(null);
  const { show, menu } = useContextMenu();
  const list = LISTS.find((l) => l.key === active) || LISTS[0];
  const p = patient();

  const rowOf = (e) => {
    const tr = e.target.closest && e.target.closest("tbody tr");
    return tr && box.current && box.current.contains(tr) ? tr : null;
  };
  const press = (el) => { if (el) el.click(); };
  const addButton = () => Array.from(box.current?.querySelectorAll("button") || []).find((b) => /^\s*add\b/i.test(b.textContent));
  const rowText = (tr) => Array.from(tr.querySelectorAll("td")).map((td) => td.textContent.trim()).filter(Boolean).join(" | ");

  const onDouble = (e) => {
    const tr = rowOf(e);
    if (!tr) return;
    const pencil = tr.querySelector(".fa-pencil, .fa-edit, .fa-pen");
    if (pencil) press(pencil);
    else toast.info(`This ${list.noun} can't be edited here. Right-click to copy it.`, { autoClose: 2500, hideProgressBar: true });
  };

  const onMenu = (e) => {
    const tr = rowOf(e);
    const add = addButton();
    const items = [];
    if (tr) {
      const pencil = tr.querySelector(".fa-pencil, .fa-edit, .fa-pen");
      const bin = tr.querySelector(".fa-trash");
      items.push({ label: `Edit this ${list.noun}`, hint: "double-click", disabled: !pencil, onClick: () => press(pencil) });
      items.push({ label: `Delete this ${list.noun}`, disabled: !bin, onClick: () => press(bin) });
      items.push({ divider: true });
    }
    items.push({ label: `Add a ${list.noun}`, disabled: !add, onClick: () => press(add) });
    if (tr) {
      items.push({
        label: "Copy this row",
        onClick: async () => {
          const ok = await copyText(rowText(tr));
          (ok ? toast.success : toast.error)(ok ? "Row copied" : "Could not copy", { autoClose: 1500, hideProgressBar: true });
        },
      });
    }
    items.push({ divider: true }, { label: "Print", onClick: () => window.print() });
    show(e, items);
  };

  const Current = list.Component;

  return (
    <div className="ox-mh">
      <MedicalHistoryNav />

      <div className="ox-seg ox-seg--wide" role="tablist" aria-label="Previous history">
        {LISTS.map((l) => (
          <button key={l.key} type="button" role="tab" aria-selected={active === l.key}
            className={`ox-seg__btn${active === l.key ? " is-active" : ""}`} onClick={() => setActive(l.key)}>
            {l.label}
          </button>
        ))}
        {p && <span className="ox-hint ox-seg__who">{[p.firstName, p.lastName].filter(Boolean).join(" ")}</span>}
      </div>

      <section className="ox-card ox-embed" ref={box} onDoubleClick={onDouble} onContextMenu={onMenu}>
        <EmbeddedContext.Provider value>
          <Current key={list.key} />
        </EmbeddedContext.Provider>
      </section>
      <p className="ox-hint">Double-click a row to edit it. Right-click for edit, delete, add and copy.</p>
      {menu}
    </div>
  );
}
